import { useEffect, useState } from "react";
import { Country } from "../store/useMarketplaceStore";
import { axiosInstance } from "../utils/axios";
import { API_URL } from "../providers/dataProvider";

export type TokenValidationState = "valid" | "invalid" | "loading" | "idle";

export const useTokenValidation = (
  selectedMarketplace: Country | undefined,
) => {
  const [tokenValidationState, setTokenValidationState] =
    useState<TokenValidationState>("idle");
  const [invalidMarketplace, setInvalidMarketplace] = useState<
    Country | undefined
  >();

  // Only validate if we have a selected marketplace with a store
  const shouldValidate = selectedMarketplace && !!selectedMarketplace.storeName;

  useEffect(() => {
    if (!shouldValidate) {
      setTokenValidationState("idle");
      setInvalidMarketplace(undefined);
      return;
    }

    setTokenValidationState("loading");

    const validateToken = async () => {
      try {
        // Mock failed request for testing - uncomment the line below to simulate token expiry
        // throw new Error("Mocked token validation failure");

        const response = await axiosInstance.get(
          `${API_URL}/authorization/validate-refresh-token/${selectedMarketplace.id}`,
        );

        if (response.status === 200) {
          setTokenValidationState("valid");
          setInvalidMarketplace(undefined);
        } else {
          setTokenValidationState("invalid");
          setInvalidMarketplace(selectedMarketplace);
        }
      } catch (error) {
        console.error(
          `Token validation failed for marketplace ${selectedMarketplace.id}:`,
          error,
        );
        setTokenValidationState("invalid");
        setInvalidMarketplace(selectedMarketplace);
      }
    };

    validateToken();
  }, [shouldValidate, selectedMarketplace?.id]);

  return {
    tokenValidationState,
    invalidMarketplace,
    isTokenValid: tokenValidationState === "valid",
    isTokenInvalid: tokenValidationState === "invalid",
    isValidating: tokenValidationState === "loading",
  };
};
